import { Box, Tooltip, type TooltipProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import { CONSOLE_DISABLED_SX } from './chrome';

interface CloseRowButtonProps {
    /** Whether closing is offered; false for the last row left in the grid. */
    readonly canClose: boolean;
    readonly onClose: () => void;
    /** The chrome of the bar or surface the button sits in. */
    readonly sx: SxProps<Theme>;
    /** Which way the reason opens, toward the free side of the host. */
    readonly tooltipPlacement: TooltipProps['placement'];
}

/**
 * Closes one dataset row. The grid always shows at least one row, so the last
 * one cannot be closed; the button stays and says why rather than
 * disappearing, the reason reachable by hover and by Tab. The glyph names
 * nothing, so an available button carries its name as a tooltip.
 */
export default function CloseRowButton({
    canClose,
    onClose,
    sx,
    tooltipPlacement,
}: CloseRowButtonProps) {
    const intl = useIntl();
    const name = intl.formatMessage({ id: 'viewportGrid.closeRow' });
    return (
        <DisabledHint
            title={
                canClose
                    ? undefined
                    : intl.formatMessage({
                          id: 'viewportGrid.closeRowUnavailable',
                      })
            }
            placement={tooltipPlacement}
            keepWrapper
            spanStyle={{ display: 'inline-flex' }}
        >
            <Tooltip title={canClose ? name : ''} placement={tooltipPlacement}>
                <Box
                    component="button"
                    type="button"
                    aria-label={name}
                    data-testid="row-close"
                    disabled={!canClose}
                    onClick={onClose}
                    sx={[
                        ...(Array.isArray(sx) ? sx : [sx]),
                        CONSOLE_DISABLED_SX,
                    ]}
                >
                    ×
                </Box>
            </Tooltip>
        </DisabledHint>
    );
}
